import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Play, Square, Settings, Power, Activity, CheckCircle2, XCircle, Clock, Check, AlertCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";
import { Bucket } from "./bucket-management";
import { useAuth } from "../components/features/AuthContext";
import { useToast } from "../hooks/useToast";

const credentialSchema = z.object({
    clientId: z.string().min(1, "Client ID is required"),
    accessToken: z.string().min(1, "Access Token is required"),
});

const bucketSchema = z.object({
    bucketIds: z.array(z.string()).min(1, "At least one bucket must be selected"),
});

type CredentialFormValues = z.infer<typeof credentialSchema>;
type BucketFormValues = z.infer<typeof bucketSchema>;

interface ServiceStatusResponse {
    tick_service: "running" | "stopped" | "not_initialized";
    historical_service: "running" | "stopped" | "not_initialized";
    binance_stream: "running" | "stopped" | "not_initialized";
}

interface ServiceConfig {
    key: string;
    label: string;
    description: string;
    port: number;
    endpoints: {
        start: string;
        stop?: string;
        status?: string;
        health?: string;
        reload?: string;
    };
    productionPrefix?: "ticks" | "crypto" | "stocks" | "crypto_ticks";
    type?: "stocks" | "crypto";
}

type ServiceAction = keyof ServiceConfig["endpoints"];

const SERVICES: ServiceConfig[] = [
    {
        key: "tick_service",
        label: "Dhan Seconds Tick Service",
        description: "Live stock market tick data feed",
        port: 8004,
        endpoints: {
            start: "control/start",
            stop: "control/stop",
            reload: "control/reload",
            status: "status",
            health: "health",
        },
        productionPrefix: "ticks",
        type: "stocks",
    },
    {
        key: "binance_tick_service",
        label: "Binance Seconds Tick Service",
        description: "Real-time cryptocurrency tick data",
        port: 8003,
        endpoints: {
            start: "control/start",
            stop: "control/stop",
            reload: "control/reload",
            status: "status",
            health: "health",
        },
        productionPrefix: "crypto_ticks",
        type: "crypto",
    },
    {
        key: "historical_service",
        label: "Stocks Minute Tick Service",
        description: "Historical stock data collection",
        port: 8004,
        endpoints: {
            start: "historical/run-now",
            stop: "historical/stop",
            reload: "historical/reload",
            status: "status",
            health: "health",
        },
        productionPrefix: "ticks",
        type: "stocks",
    },
    {
    key: "binance_historical_service",
    label: "Binance Historical Data Service",
    description: "Historical cryptocurrency data collection",
    port: 8003,
    endpoints: {
        start: "historical/run-now",
        stop: "historical/stop",
        reload: "historical/reload",
        status: "status",
        health: "health",
    },
    productionPrefix: "crypto_ticks",
    type: "crypto",
},
];


export default function ServicesManagement() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
    const [bucketDialogOpen, setBucketDialogOpen] = useState(false);
    const [editingService, setEditingService] = useState<string | null>(null);

    const credentialForm = useForm<CredentialFormValues>({
        resolver: zodResolver(credentialSchema),
        defaultValues: {
            clientId: "",
            accessToken: "",
        },
    });

    const bucketForm = useForm<BucketFormValues>({
        resolver: zodResolver(bucketSchema),
        defaultValues: {
            bucketIds: [],
        },
    });

    const startAllServices = async () => {
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        try {
            if (isLocalhost) {
                await Promise.all(
                    SERVICES.filter((s) => s.endpoints.start).map((service) =>
                        apiRequest("POST", service.endpoints.start, undefined, undefined, undefined, service.port)
                    )
                );
            } else {
                await Promise.all(
                    SERVICES.filter((s) => s.endpoints.start).map((service) => {
                        const fullPath = service.productionPrefix
                            ? `${service.productionPrefix}/${service.endpoints.start}`
                            : service.endpoints.start;
                        return apiRequest("POST", fullPath);
                    })
                );
            }

            toast({ title: "Success", description: "All services started successfully" });
            queryClient.invalidateQueries({ queryKey: ["service-status"] });
        } catch (err) {
            toast({ title: "Error", description: "Failed to start all services", variant: "destructive" });
        }
    };

    const stopAllServices = async () => {
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        try {
            if (isLocalhost) {
                await Promise.all(
                    SERVICES.filter((s) => s.endpoints.stop).map((service) =>
                        apiRequest("POST", service.endpoints.stop!, undefined, undefined, undefined, service.port)
                    )
                );
            } else {
                await Promise.all(
                    SERVICES.filter((s) => s.endpoints.stop).map((service) => {
                        const fullPath = service.productionPrefix
                            ? `${service.productionPrefix}/${service.endpoints.stop}`
                            : service.endpoints.stop!;
                        return apiRequest("POST", fullPath);
                    })
                );
            }

            toast({ title: "Success", description: "All services stopped successfully" });
            queryClient.invalidateQueries({ queryKey: ["service-status"] });
        } catch (err) {
            toast({ title: "Error", description: "Failed to stop all services", variant: "destructive" });
        }
    };

    const { data: statuses } = useQuery({
        queryKey: ["service-status"],
        queryFn: async () => {
            const isLocalhost = window.location.hostname === "localhost" ||
                window.location.hostname === "127.0.0.1";

            const statusPromises = SERVICES.map(async (service) => {
                if (!service.endpoints.status) {
                    return { key: service.key, status: "not_initialized" as const };
                }

                try {
                    let response;

                    if (isLocalhost) {
                        // Development: use port-based routing
                        response = await apiRequest<any>(
                            "GET",
                            service.endpoints.status,
                            undefined,
                            undefined,
                            undefined,
                            service.port
                        );
                    } else {
                        // Production: use prefix-based routing
                        const fullPath = service.productionPrefix
                            ? `${service.productionPrefix}/${service.endpoints.status}`
                            : service.endpoints.status;

                        response = await apiRequest<any>("GET", fullPath);
                    }

                    return {
                        key: service.key,
                        status: response[service.key] || "stopped"
                    };
                } catch (error) {
                    console.error(`Failed to fetch status for ${service.key}:`, error);
                    return { key: service.key, status: "stopped" as const };
                }
            });

            const results = await Promise.all(statusPromises);

            // Convert array to object
            return results.reduce((acc, { key, status }) => {
                acc[key as keyof ServiceStatusResponse] = status;
                return acc;
            }, {} as ServiceStatusResponse);
        },
        refetchInterval: 120000,
        staleTime: 60000, // Consider data fresh for 1 minute
        retry: 2, // Retry failed requests twice
    });

    const { data: credentialsInfo } = useQuery({
        queryKey: ["global-credentials"],
        queryFn: async () => {
            const response = await apiRequest<ApiResponse<{
                updated_at: string | null;
                has_credentials: boolean;
            }>>("GET", "api/services/credentials");
            return response.data;
        },
    });

    const {
        data: dhan_api_test_result,
        error: testError,
        refetch: testCredentials,
        isFetching: testingCredentials,
    } = useQuery({
        queryKey: ["dhan_api_test"],
        queryFn: async () => {
            const response = await apiRequest<ApiResponse<any>>("GET", "api/test_dhan_data_api", undefined, undefined, "stocks", undefined);
            return response;
        },
        enabled: false,
    });

    const toggleMutation = useMutation({
        mutationFn: async ({ serviceConfig, action }: { serviceConfig: ServiceConfig; action: ServiceAction }) => {
            const endpoint = serviceConfig.endpoints[action];
            if (!endpoint) {
                throw new Error(`${action} action not supported for ${serviceConfig.label}`);
            }

            const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

            if (isLocalhost) {
                await apiRequest("POST", endpoint, undefined, undefined, undefined, serviceConfig.port);
            } else {
                const fullPath = serviceConfig.productionPrefix
                    ? `${serviceConfig.productionPrefix}/${endpoint}`
                    : endpoint;
                await apiRequest("POST", fullPath);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["service-status"] });
            toast({ title: "Success", description: "Service status updated" });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to update service", variant: "destructive" });
        },
    });

    const credentialsMutation = useMutation({
        mutationFn: async (values: CredentialFormValues) => {
            await apiRequest("POST", "api/services/credentials", {
                client_id: values.clientId,
                access_token: values.accessToken,
            });
        },
        onSuccess: () => {
            toast({ title: "Success", description: "Credentials updated successfully" });
            setCredentialsDialogOpen(false);
            credentialForm.reset();
            queryClient.invalidateQueries({ queryKey: ["global-credentials"] });
            queryClient.invalidateQueries({ queryKey: ["dhan_api_test"] });
        },
        onError: () => {
            toast({ title: "Error", description: "Failed to update credentials", variant: "destructive" });
        },
    });

    const bucketsMutation = useMutation({
        mutationFn: async ({ serviceKey, values }: { serviceKey: string; values: BucketFormValues }) => {
            await apiRequest("POST", `api/services/${serviceKey}/buckets`, {
                bucket_ids: values.bucketIds,
            });
        },
        onSuccess: () => {
            toast({ title: "Success", description: "Buckets updated successfully" });
            setBucketDialogOpen(false);
            bucketForm.reset();
            if (editingService) {
                queryClient.invalidateQueries({ queryKey: ["service-buckets", editingService] });
            }
        },
        onError: () => {
            toast({ title: "Error", description: "Failed to update buckets", variant: "destructive" });
        },
    });

    const handleCredentialsSubmit = (data: CredentialFormValues) => {
        credentialsMutation.mutate(data);
    };

    const handleBucketsSubmit = (data: BucketFormValues) => {
        if (!editingService) return;
        bucketsMutation.mutate({ serviceKey: editingService, values: data });
    };

    function formatRelativeTime(dateString: string) {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSeconds = Math.floor(diffMs / 1000);

        let relativeStr: string;

        if (diffSeconds < 0) {
            relativeStr = "just now";
        } else if (diffSeconds < 60) {
            relativeStr = "just now";
        } else if (diffSeconds < 3600) {
            const mins = Math.floor(diffSeconds / 60);
            relativeStr = `${mins} min${mins > 1 ? "s" : ""} ago`;
        } else if (diffSeconds < 86400) {
            const hrs = Math.floor(diffSeconds / 3600);
            relativeStr = `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
        } else {
            const days = Math.floor(diffSeconds / 86400);
            relativeStr = `${days} day${days > 1 ? "s" : ""} ago`;
        }

        const timeStr = date.toLocaleString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Asia/Kolkata",
        });

        const dateStr = date.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            timeZone: "Asia/Kolkata",
        });

        return { timeStr, dateStr, relativeStr };
    }

    const runningCount = SERVICES.filter((s) => statuses?.[s.key as keyof ServiceStatusResponse] === "running").length;
    const timeInfo = credentialsInfo?.updated_at ? formatRelativeTime(credentialsInfo.updated_at) : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Header Section */}
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Services Management</h1>
                        <p className="text-slate-500 flex items-center gap-2">
                            <Activity className="w-4 h-4" />
                            Monitor and control your backend services
                        </p>
                    </div>

                    <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 shadow-sm border border-slate-200">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-slate-700">
                                {runningCount} / {SERVICES.length}
                            </span>
                        </div>
                        <span className="text-xs text-slate-500">Services Active</span>
                    </div>
                </div>

                {/* Control Panel */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                <Power className="w-5 h-5 text-slate-700" />
                            </div>
                            <div>
                                <h2 className="font-semibold text-slate-900">Global Controls</h2>
                                <p className="text-sm text-slate-500">Manage all services simultaneously</p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <Button onClick={startAllServices} className="bg-green-600 hover:bg-green-700 text-white shadow-sm">
                                <Play className="w-4 h-4 mr-2" />
                                Start All
                            </Button>

                            <Button
                                onClick={stopAllServices}
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            >
                                <Square className="w-4 h-4 mr-2" />
                                Stop All
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Credentials Management Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-slate-900">Dhan API Credentials</h3>
                                {credentialsInfo?.has_credentials ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                ) : (
                                    <AlertCircle className="w-4 h-4 text-amber-500" />
                                )}
                            </div>
                            <p className="text-sm text-slate-500 mb-3">Manage API credentials and test connectivity</p>
                            {timeInfo && (
                                <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    <span>Last updated {timeInfo.relativeStr}</span>
                                    <span className="text-slate-400">
                                        • {timeInfo.dateStr} {timeInfo.timeStr} IST
                                    </span>
                                </div>
                            )}
                        </div>

                        <Badge
                            variant={credentialsInfo?.has_credentials ? "default" : "secondary"}
                            className={`${credentialsInfo?.has_credentials
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                                } border`}
                        >
                            {credentialsInfo?.has_credentials ? "Configured" : "Not Set"}
                        </Badge>
                    </div>

                    <div className="flex gap-2">
                        <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="flex-1 border-slate-200 hover:bg-slate-50">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Update Credentials
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Update API Credentials</DialogTitle>
                                </DialogHeader>

                                <Form {...credentialForm}>
                                    <form onSubmit={credentialForm.handleSubmit(handleCredentialsSubmit)} className="space-y-4">
                                        <FormField
                                            control={credentialForm.control}
                                            name="clientId"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Client ID</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Enter Client ID" {...field} className="border-slate-200" />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={credentialForm.control}
                                            name="accessToken"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Access Token</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Enter Access Token" {...field} className="border-slate-200" />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <div className="flex justify-end gap-2 pt-4">
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={() => setCredentialsDialogOpen(false)}
                                                disabled={credentialsMutation.isPending}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={credentialsMutation.isPending}
                                                className="bg-slate-900 hover:bg-slate-800 text-gray-100 "
                                            >
                                                {credentialsMutation.isPending ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save Changes"
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </Form>
                            </DialogContent>
                        </Dialog>

                        <Button
                            onClick={() => testCredentials()}
                            disabled={testingCredentials}
                            variant="outline"
                            className="border-slate-200 hover:bg-slate-50"
                        >
                            {testingCredentials ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2"></div>
                                    Testing...
                                </>
                            ) : (
                                <>
                                    <Play className="w-4 h-4 mr-2" />
                                    Test Connection
                                </>
                            )}
                        </Button>
                    </div>

                    {testError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
                            <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            <p className="text-sm text-red-700">{(testError as Error).message}</p>
                        </div>
                    )}

                    {dhan_api_test_result?.message && (
                        <div
                            className={`flex items-center gap-2 p-3 rounded-lg border mt-4 ${dhan_api_test_result.status === "error"
                                ? "bg-red-50 border-red-200"
                                : "bg-green-50 border-green-200"
                                }`}
                        >
                            {dhan_api_test_result.status === "error" ? (
                                <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            ) : (
                                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                            )}
                            <p
                                className={`text-sm ${dhan_api_test_result.status === "error" ? "text-red-700" : "text-green-700"
                                    }`}
                            >
                                {dhan_api_test_result.message}
                            </p>
                        </div>
                    )}
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {SERVICES.map((service) => {
                        let service_status = statuses?.[service.key as keyof ServiceStatusResponse]
                        let running = service_status == "running"

                        return (
                            <div
                                key={service.key}
                                className="group bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="font-semibold text-slate-900">{service.label}</h3>
                                            {running ? (
                                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-slate-400" />
                                            )}
                                        </div>
                                        <p className="text-sm text-slate-500">{service.description}</p>
                                    </div>

                                    <Badge
                                        variant={running ? "default" : "secondary"}
                                        className={`${running
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : "bg-slate-100 text-slate-600 border-slate-200"
                                            } border`}
                                    >
                                        {running ? "Running" : service_status ?? "stopped"}
                                    </Badge>
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        onClick={() =>
                                            toggleMutation.mutate({
                                                serviceConfig: service,
                                                action: running ? "stop" : "start",
                                            })
                                        }
                                        disabled={toggleMutation.isPending}
                                        variant="outline"
                                        className={`flex-1 ${running
                                            ? "border-red-200 text-red-600 hover:bg-red-50"
                                            : "border-green-200 text-green-600 hover:bg-green-50"
                                            }`}
                                    >
                                        {running ? (
                                            <>
                                                <Square className="w-4 h-4 mr-2" />
                                                Stop
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-4 h-4 mr-2" />
                                                Start
                                            </>
                                        )}
                                    </Button>

                                    <Button
                                        disabled={!service.endpoints.reload || toggleMutation.isPending}
                                        onClick={() =>
                                            toggleMutation.mutate({
                                                serviceConfig: service,
                                                action: "reload",
                                            })
                                        }
                                        className={`flex-1 ${running
                                            ? "border-red-200 text-red-600 hover:bg-red-50"
                                            : "border-green-200 text-green-600 hover:bg-green-50"
                                            }`}
                                    >
                                        Reload
                                    </Button>

                                    <Dialog
                                        open={bucketDialogOpen && editingService === service.key}
                                        onOpenChange={setBucketDialogOpen}
                                    >
                                        <DialogTrigger asChild>
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setEditingService(service.key);
                                                    setBucketDialogOpen(true);
                                                }}
                                                className="border-slate-200 hover:bg-slate-50"
                                            >
                                                <Settings className="w-4 h-4" />
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Configure Buckets - {service.label}</DialogTitle>
                                            </DialogHeader>

                                            <BucketsForm
                                                serviceKey={service.key}
                                                form={bucketForm}
                                                handleSubmit={handleBucketsSubmit}
                                                isPending={bucketsMutation.isPending}
                                                onCancel={() => setBucketDialogOpen(false)}
                                                formatRelativeTime={formatRelativeTime}
                                                user={user}
                                                currentTradingMode={service.type === "stocks" ? "stocks" : "crypto"}
                                            />

                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}



function BucketsForm({
    serviceKey,
    form,
    handleSubmit,
    isPending,
    onCancel,
    formatRelativeTime,
    user,
    currentTradingMode,
}: {
    serviceKey: string;
    form: any;
    handleSubmit: (data: BucketFormValues) => void;
    isPending: boolean;
    onCancel: () => void;
    formatRelativeTime: (dateString: string) => { timeStr: string; dateStr: string; relativeStr: string };
    user: { userId: string; role: string } | null;
    currentTradingMode: string | null;
}) {
    const { data: buckets, isLoading: bucketsLoading } = useQuery({
        queryKey: ["api/buckets", user?.userId, user?.role, currentTradingMode],
        queryFn: async () => {
            if (!user?.userId || !user?.role || !currentTradingMode) return [];
            const url = `api/buckets?userId=${user.userId}&role=${user.role}&category=${currentTradingMode}`;
            const res = await apiRequest<ApiResponse<Bucket[]>>("GET", url);
            return res.data;
        },
        enabled: !!user?.userId && !!user?.role && !!currentTradingMode,
    });

    const { data: bucketConfig } = useQuery({
        queryKey: ["service-buckets", serviceKey],
        queryFn: async () => {
            const response = await apiRequest<ApiResponse<{
                updated_at: string | null;
                bucket_ids: string[];
            }>>("GET", `api/services/${serviceKey}/buckets`);
            return response.data;
        },
    });

    useEffect(() => {
        if (bucketConfig) {
            form.reset({
                bucketIds: bucketConfig.bucket_ids || [],
            });
        }
    }, [bucketConfig, form]);

    const timeInfo = bucketConfig?.updated_at ? formatRelativeTime(bucketConfig.updated_at) : null;

    return (
        <div className="space-y-6 max-w-full min-w-0 overflow-x-hidden">
            {timeInfo && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>Last updated</span>
                    <span className="font-medium">{timeInfo.relativeStr}</span>
                    <span className="text-slate-400">
                        • {timeInfo.dateStr} {timeInfo.timeStr} IST
                    </span>
                </div>
            )}
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="bucketIds"
                        render={({ field }) => {
                            const selectedBuckets = (field.value as string[]) || [];

                            return (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">Select Buckets</FormLabel>
                                    <div className="space-y-2">
                                        {bucketsLoading ? (
                                            <div className="flex items-center gap-2 p-2">
                                                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                                                <span className="text-sm text-slate-600">Loading buckets...</span>
                                            </div>
                                        ) : (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        role="combobox"
                                                        className="w-full max-w-full justify-between bg-white border-slate-200 hover:bg-slate-50 h-10"
                                                    >
                                                        <span className="truncate block overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left">
                                                            {selectedBuckets.length > 0
                                                                ? buckets
                                                                    ?.filter((b) => selectedBuckets.includes(b._id))
                                                                    .map((b) => b.name)
                                                                    .join(", ") || "Select Buckets"
                                                                : "Select Buckets"}
                                                        </span>
                                                        <Check className="ml-2 h-4 w-4 opacity-50 flex-shrink-0" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-3 max-h-64 overflow-y-auto">
                                                    <div className="space-y-2">
                                                        {buckets?.length === 0 ? (
                                                            <div className="text-center py-4 text-slate-500 text-sm">
                                                                No buckets available
                                                            </div>
                                                        ) : (
                                                            buckets?.map((bucket) => {
                                                                const checked = selectedBuckets.includes(bucket._id);
                                                                return (
                                                                    <div
                                                                        key={bucket._id}
                                                                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-all"
                                                                        onClick={() => {
                                                                            const newValue = checked
                                                                                ? selectedBuckets.filter((id) => id !== bucket._id)
                                                                                : [...selectedBuckets, bucket._id];
                                                                            field.onChange(newValue);
                                                                        }}
                                                                    >
                                                                        <Checkbox
                                                                            checked={checked}
                                                                            className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 flex-shrink-0"
                                                                        />
                                                                        <div className="flex-1 min-w-0">
                                                                            <span className="text-sm font-medium text-slate-900 block truncate">
                                                                                {bucket.name}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                        )}
                                        {selectedBuckets.length > 0 && (
                                            <div className="text-xs text-slate-500">
                                                {selectedBuckets.length} bucket{selectedBuckets.length > 1 ? "s" : ""} selected
                                            </div>
                                        )}
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />

                    <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" type="button" onClick={onCancel} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending} className="bg-slate-900 hover:bg-slate-800 text-gray-100 ">
                            {isPending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
}

